import { FC, useEffect, useState } from "react";
import DashboardContentLayout from "../../components/dashboard-content-layout";
import useRegionManagementStore from "../../../../state/region-management-store";
import { Box, Button, Typography } from "@mui/material";
import { makeSxStyles } from "ui";
import { InfoOutlined, DoneOutlined } from "@mui/icons-material";
import RegionManagementSidebarSequence from "../region-management-sidebar/region-management-sidebar-sequence";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AdRoutes } from "../../../../constants/ad-routes";
import {
  useAdminRegionV2ControllerAddLocationsToRegion,
  useAdminRegionV2ControllerUpdateRegionLocationsSequence,
} from "service";
import { toast } from "react-toastify";
import { queryClient } from "../../../../query-client";
import {
  createRegionsInvalidationPredicate,
  createLocationsInvalidationPredicate,
} from "service/api/estabrookQueryKeys";
import { AdminPatchRegionLocationSequenceReqDto } from "service/api/estabrookSchemas";
import MapComponent from "../../../../components/map/map";
import { MapComponentProps } from "../../../../components/map/map.types";
import MapProvider from "../../../../components/map/map-provider";
import Navbar from "../../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../../shared/components/header-items/HeaderItems";
import { useDialogStore } from "../../../../state/dialog-store";
import { useBlocker } from "react-router-dom";
import RegionWorkflowStepper from "../../../../components/region-workflow-stepper";

const CreateSequencePage: FC = () => {
  const {
    selectedAddresses,
    sequencedAddresses,
    remainingSequences,
    moveValues,
    allSequenced,
    mapZoom,
    selectedAddressesForSequencing,
    sidebar,
    setMoveValues,
    lastSequence,
    setAddressSequence,
    resetAllSelected,
    setQuery,
    setSelectedAddress,
    setMapZoom,
    toggleDrawing,
    setSidebar,
    setMapCentering,
    activeRegionShortDetails,
    setRegionSequencing,
    setPendingFocusRegionId,
    setAllSequenced,
  } = useRegionManagementStore();
  const styles = useStyles();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<
    AdminPatchRegionLocationSequenceReqDto[]
  >([]);

  const { regionId } = useParams<{
    regionId: string;
  }>();
  const location = useLocation();
  const addUnassignedToRegion = location.pathname.includes(
    "add-unassigned-to-region",
  );
  const resequenceRegionPath = location.pathname.includes("re-sequence");
  const [createSequence, setCreateSequence] = useState(false);
  const [completeSequence, setCompleteSequence] = useState(false);
  const { mutate: updateSequence } =
    useAdminRegionV2ControllerUpdateRegionLocationsSequence({
      onSuccess: async () => {
        toast.success(
          `Region ${sequencedAddresses[0].region?.name} Resequenced`,
        );
        await queryClient.invalidateQueries(
          createRegionsInvalidationPredicate(),
        );
        await queryClient.invalidateQueries(
          createLocationsInvalidationPredicate(),
        );

        // Set the region ID that should be focused when the region management page loads
        if (regionId) {
          setTimeout(() => {
            setPendingFocusRegionId(regionId);
            navigate(AdRoutes.regionManagement);
            resetAllSelected();
          }, 500);
        } else {
          navigate(AdRoutes.regionManagement);
          resetAllSelected();
        }
      },
    });

  const { mutate: patchLocations } =
    useAdminRegionV2ControllerAddLocationsToRegion({
      onSuccess: async () => {
        setMoveValues({
          region: "",
          order: "",
          sequence: 0,
        });
        toast.success(
          "Locations Moved Successfully. Future Routes have been modified to the new Region Template. Live Route are unaffected.",
        );
        await queryClient.invalidateQueries(
          createRegionsInvalidationPredicate(),
        );
        await queryClient.invalidateQueries(
          createLocationsInvalidationPredicate(),
        );
        resetAllSelected();
      },
    });

  // TODO add a validator that if there are no addresses selected
  // to return to the region management page

  useEffect(() => {
    if (!selectedAddresses || selectedAddresses.length === 0) {
      navigate(AdRoutes.regionManagement);
    }
  }, [selectedAddresses]);

  const updateLocations = () => {
    const updatedLocations = locations.map((item) => ({
      id: item.locationId,
      seqNumber: item.seqNumber,
    }));

    setCompleteSequence(true);

    updateSequence({
      body: {
        locations: updatedLocations,
      },
      pathParams: {
        id: regionId || "",
      },
    });
  };

  const patchLocationsSequence = () => {
    if (!regionId) {
      toast.error("Please select a region before moving addresses.");
      return;
    }

    patchLocations({
      body: {
        locations: locations,
        targetSeqNumber: moveValues.sequence,
        transferMode: moveValues.order as "before" | "after" | "append",
      },
      pathParams: {
        id: regionId,
      },
    });

    setCompleteSequence(true);
  };

  const onEditSequence = () => {
    addUnassignedToRegion ? patchLocationsSequence() : updateLocations();
  };

  const onCreateSequence = () => {
    setMapCentering(undefined, sequencedAddresses);
    setCreateSequence(true);
  };

  const onNextStep = () => {
    regionId ? onEditSequence() : onCreateSequence();
  };

  useEffect(() => {
    if (allSequenced) {
      // For both flows, use sequencedAddresses since that's what contains the user-sequenced addresses
      const filteredLocations = sequencedAddresses.map((item) => ({
        locationId: item.id,
        seqNumber: Number(item.locationSequence),
      }));

      if (filteredLocations.length > 0) {
        setLocations(filteredLocations);
      }
    }
  }, [sequencedAddresses, allSequenced]);

  // Custom unsaved changes blocker that resets regionSequencing when user confirms leaving
  const { setDialog } = useDialogStore();
  const blocker = useBlocker(
    addUnassignedToRegion || resequenceRegionPath
      ? !completeSequence
      : !createSequence,
  );

  useEffect(() => {
    if (blocker.state === "blocked") {
      setDialog({
        title: "Unsaved changes",
        description:
          "You have unsaved changes. Are you sure you want to leave?",
        confirmText: "Leave",
        cancelText: "Cancel",
        withCancelAction: true,
        onConfirm: async (onClose) => {
          onClose();
          // Reset the regionSequencing state to prevent redirect loop
          setRegionSequencing("");
          setAllSequenced(false);
          blocker.proceed();
        },
        onClose: async () => {
          blocker.reset();
        },
      });
    }
  }, [blocker, setDialog, setRegionSequencing]);

  useEffect(() => {
    if (createSequence) {
      navigate(AdRoutes.createRegionDetails);
    }
  }, [createSequence]);

  const preventRefresh = (event: BeforeUnloadEvent) => {
    if (!allSequenced) {
      event.preventDefault();
      event.returnValue = "";
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", preventRefresh);

    return () => {
      window.removeEventListener("beforeunload", preventRefresh);
    };
  }, []);

  const mapProps: MapComponentProps = {
    mapWidth: "100%",
    mapHeight: "708px",
    markers: addUnassignedToRegion
      ? selectedAddressesForSequencing
      : selectedAddresses,
    mapZoom,
    showTopMapControl: false,
    lastSequence: lastSequence,
    selectedAddresses: selectedAddresses,
    isLoading: false,
    isSearchLoading: false,
    sequencing: true,
    sequencingUnassigned: addUnassignedToRegion,
    setQuery: setQuery,
    setAddressSequence: setAddressSequence,
    setSelectedAddress: setSelectedAddress,
    onMapZoomChange: setMapZoom,
    onDrawingToggle: toggleDrawing,
    setSidebar,
  };

  return (
    <>
      <DashboardContentLayout>
        <Navbar showAccountMenu>
          <HeaderItems>
            <HeaderItemsLeft
              breadcrumbs={[
                {
                  label: "Region Management",
                  url: AdRoutes.regionManagement,
                },
                {
                  label: "Sequence Addresses",
                },
              ]}
            />
            <HeaderItemsRight>
              <Button
                disabled={!allSequenced}
                variant="contained"
                onClick={onNextStep}
                data-testid="save-sequence-button"
              >
                {regionId ? "Confirm Sequence" : "Create Region"}
              </Button>
            </HeaderItemsRight>
          </HeaderItems>
        </Navbar>

        <RegionWorkflowStepper />

        <Box>
          {!!activeRegionShortDetails && regionId && (
            <Box sx={styles.regionDetailsSection}>
              <Box
                sx={styles.regionColor}
                bgcolor={activeRegionShortDetails?.color}
              />
              <Typography variant="h5" fontWeight="bold" color="#C5C6C9">
                {activeRegionShortDetails?.name}
              </Typography>
            </Box>
          )}
        </Box>
        <Box display="flex">
          <Box sx={sidebar ? styles.hasSidebar : styles.noSidebar}>
            <MapProvider>
              <MapComponent {...mapProps} />
            </MapProvider>
          </Box>

          {sidebar && (
            <RegionManagementSidebarSequence
              customHeader={
                <Box sx={styles.sideBarTitleInfo}>
                  <Typography>
                    Sequence routes by clicking on each address on the map,
                    starting with the first and ending with the last.
                  </Typography>
                  <Box sx={styles.infoBar}>
                    {!allSequenced ? (
                      <>
                        <InfoOutlined
                          style={{ marginRight: "8px", color: "#8ACEFF" }}
                        />
                        <Typography>
                          {remainingSequences === 0
                            ? selectedAddresses && selectedAddresses.length
                            : remainingSequences}{" "}
                          addresses to be sequenced.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <DoneOutlined
                          style={{
                            marginRight: "8px",
                            color: "rgba(63, 191, 158, 1)",
                          }}
                        />
                        <Typography sx={{ fontSize: "13px" }}>
                          Sequence is ready to be confirmed.
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              }
            />
          )}
        </Box>
      </DashboardContentLayout>
    </>
  );
};

const useStyles = makeSxStyles(({ palette }) => ({
  hasSidebar: {
    width: "calc(100% - 317px)",
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 40px",
    bgcolor: "#1A1C1E",
  },
  regionDetailsSection: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  sideBarTitleInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingBottom: "15px",
    borderBottom: "1px solid rgba(65, 71, 77, 1)",
  },
  customActionsWrapper: {
    display: "flex",
    flexGrow: "1",
  },
  stepperContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  orderWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",

    "& span": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginRight: "10px",
      width: "24px",
      height: "24px",
      borderRadius: "100%",
      backgroundColor: palette.grey[500],
    },

    "& span, & p": {
      color: palette.grey[300],
    },

    "&.active * ": {
      color: "#FFF",
    },
  },

  stepperDash: {
    display: "flex",
    margin: "0 8px",
    width: "26.29px",
    height: "1px",
    backgroundColor: palette.grey[200],
  },

  infoBar: {
    display: "flex",
    flexDirection: "row",
    marginTop: "12px",
    alignItems: "center",
  },
  regionColor: {
    width: "6px",
    height: "20px",
  },
}));

export default CreateSequencePage;
